import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Upload, AlertCircle, CheckCircle2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface CSVRow {
  country: string;
  city?: string;
  open_close: string;
  notes?: string;
}

interface ImportResult {
  opened: number;
  closed: number;
  errors: Array<{ row: number; country: string; error: string }>;
}

interface Props {
  companyId: string;
  onImportComplete: () => void;
}

const CSVDestinationImport = ({ companyId, onImportComplete }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const { toast } = useToast();

  const validateAndParseCSV = (csvText: string): CSVRow[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV vide ou invalide');
    }

    const header = lines[0].toLowerCase().split(',').map(h => h.trim());
    const rows: CSVRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row: any = {};
      
      header.forEach((key, index) => {
        row[key] = values[index] || '';
      });

      if (row.country) {
        rows.push({
          country: row.country,
          city: row.city || undefined,
          open_close: row.open_close || row['open/close'] || '',
          notes: row.notes || undefined
        });
      }
    }

    return rows;
  };

  const validateCountryWithGooglePlaces = async (countryName: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('get-google-maps-key');
      if (error) return false;

      const service = new google.maps.places.AutocompleteService();
      return new Promise((resolve) => {
        service.getPlacePredictions(
          {
            input: countryName,
            types: ['country']
          },
          (predictions, status) => {
            resolve(status === 'OK' && predictions && predictions.length > 0);
          }
        );
      });
    } catch {
      return false;
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez sélectionner un fichier CSV"
      });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const text = await file.text();
      const rows = validateAndParseCSV(text);

      const result: ImportResult = {
        opened: 0,
        closed: 0,
        errors: []
      };

      // Get user info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      // Get all countries from database
      const { data: allCountries } = await supabase
        .from('countries')
        .select('id, name');

      const countryMap = new Map(
        (allCountries || []).map(c => [c.name.toLowerCase(), c.id])
      );

      // Process each row
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const countryId = countryMap.get(row.country.toLowerCase());

        if (!countryId) {
          result.errors.push({
            row: i + 2,
            country: row.country,
            error: 'Pays non trouvé dans la base de données'
          });
          continue;
        }

        const shouldOpen = row.open_close.toLowerCase() === 'open';

        if (shouldOpen) {
          // Open country - insert or update company_countries
          const { error } = await supabase
            .from('company_countries')
            .upsert({
              company_id: companyId,
              country_id: countryId,
              is_open: true,
              notes: row.notes,
              opened_at: new Date().toISOString(),
              opened_by: user.id
            }, {
              onConflict: 'company_id,country_id'
            });

          if (error) {
            result.errors.push({
              row: i + 2,
              country: row.country,
              error: error.message
            });
          } else {
            result.opened++;
          }
        } else if (row.open_close.toLowerCase() === 'close') {
          // Close country - update or delete
          const { error } = await supabase
            .from('company_countries')
            .update({
              is_open: false,
              closed_at: new Date().toISOString(),
              closed_by: user.id
            })
            .eq('company_id', companyId)
            .eq('country_id', countryId);

          if (!error) {
            result.closed++;
          }
        }
      }

      // Save import history
      await supabase
        .from('destination_import_export_history')
        .insert({
          company_id: companyId,
          admin_user_id: user.id,
          operation_type: 'import',
          file_name: file.name,
          countries_opened: result.opened,
          countries_closed: result.closed,
          total_processed: rows.length,
          errors_count: result.errors.length,
          error_details: result.errors.length > 0 ? result.errors : null
        });

      setImportResult(result);
      onImportComplete();

      toast({
        title: "Import terminé",
        description: `${result.opened} pays ouverts, ${result.closed} pays fermés, ${result.errors.length} erreurs`
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur d'import",
        description: error.message
      });
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Country,City,Open/Close,Notes
France,,open,Beautiful country
Spain,Barcelona,open,Great for remote work
Germany,,close,Not available yet`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_import_destinations.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>
        <Upload className="mr-2 h-4 w-4" />
        Importer un fichier CSV
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importer des destinations via CSV</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Format CSV requis:</strong> Country, City (optionnel), Open/Close, Notes (optionnel)
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadTemplate} className="flex-1">
                Télécharger le modèle CSV
              </Button>
              <div className="flex-1">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                  disabled={importing}
                />
                <label htmlFor="csv-upload">
                  <Button 
                    variant="default" 
                    className="w-full" 
                    disabled={importing}
                    asChild
                  >
                    <span>
                      {importing ? 'Import en cours...' : 'Sélectionner un fichier CSV'}
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {importResult && (
              <Card className="p-4 space-y-3">
                <h3 className="font-semibold">Résumé de l'import</h3>
                
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>{importResult.opened} pays ouverts</span>
                </div>

                <div className="flex items-center gap-2 text-blue-600">
                  <X className="h-4 w-4" />
                  <span>{importResult.closed} pays fermés</span>
                </div>

                {importResult.errors.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      <span>{importResult.errors.length} erreurs</span>
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1 text-sm">
                      {importResult.errors.map((err, idx) => (
                        <div key={idx} className="text-red-600">
                          Ligne {err.row}: {err.country} - {err.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CSVDestinationImport;
